import clsx from 'clsx';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCommit } from '../lib/act.ts';
import { draftOf } from '../lib/feedback.ts';
import { PageSection } from './pages/Sections.tsx';
import { Button } from './primitives/button.tsx';
import { Card } from './primitives/shell.tsx';

interface FeedbackState {
  draft: string;
  setDraft: (next: string) => void;
}

const FeedbackContext = createContext<FeedbackState>({ draft: '', setDraft: () => {} });

/** The draft lives above the account page, so leaving it does not discard what was typed. */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [draft, setDraft] = useState('');
  const state = useMemo(() => ({ draft, setDraft }), [draft]);
  return <FeedbackContext.Provider value={state}>{children}</FeedbackContext.Provider>;
}

export const useFeedback = (): FeedbackState => useContext(FeedbackContext);

/** On the account page, at `#feedback`. */
export function Feedback() {
  const { draft, setDraft } = useContext(FeedbackContext);
  return (
    <PageSection
      id="feedback"
      title="Tell the admins"
      blurb="Anything broken, missing or confusing. It reaches them as you wrote it."
    >
      <Card>
        <Composer draft={draft} setDraft={setDraft} />
      </Card>
    </PageSection>
  );
}

function Composer({ draft, setDraft }: { draft: string; setDraft: (next: string) => void }) {
  const { commit, busy } = useCommit();
  const id = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const state = draftOf(draft);

  // Only when a link asked for the form: on the account page it waits to be wanted.
  useEffect(() => {
    const box = field.current;
    if (!box || window.location.hash !== '#feedback') return;
    box.focus();
    box.setSelectionRange(box.value.length, box.value.length);
  }, []);

  const send = useCallback(async () => {
    if (!state.sendable || busy) return;
    await commit(
      { do: 'submit-feedback', body: state.body },
      {
        // Not optimistic: clear only on success, so a refusal keeps the text.
        onOk: () => setDraft(''),
      },
    );
  }, [state.sendable, state.body, busy, commit, setDraft]);

  return (
    <>
      <label htmlFor={id} className="sr-only">
        Your note to the admins
      </label>
      <textarea
        ref={field}
        id={id}
        value={draft}
        rows={5}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            void send();
          }
        }}
        placeholder="The exit ticket rounds my quantity down…"
        className={clsx(
          'w-full resize-y rounded-control border bg-surface px-2.5 py-2 text-xs leading-relaxed text-ink outline-none placeholder:text-ink-faint',
          state.over ? 'border-warn focus:border-warn' : 'border-line focus:border-accent',
        )}
      />

      <div className="flex items-center justify-between gap-2">
        <span
          aria-live="polite"
          className={clsx('tnum text-xs', state.over ? 'text-warn' : 'text-ink-faint')}
        >
          {state.counting &&
            (state.over
              ? `${-state.remaining} over the limit`
              : `${state.remaining} characters left`)}
        </span>
        <Button tone="primary" busy={busy} disabled={!state.sendable} onClick={() => void send()}>
          Send
        </Button>
      </div>
    </>
  );
}
